import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article?.categorySlug && a.slug !== slug)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="w-full min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 text-sm hover:underline">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb />

        <article className="w-full pt-8 pb-14 md:pb-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="mb-6">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4 hover:bg-accent-200 transition-colors duration-200"
              >
                <i className="ri-folder-line"></i>
                {article.category}
              </Link>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </div>

            <div className="prose prose-sm md:prose-base max-w-none">
              <div
                className="text-foreground-700 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200 font-medium"
              >
                <i className="ri-arrow-left-line"></i>
                返回 {article.category}
              </Link>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-14">
                <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group block p-4 bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                    >
                      <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {related.excerpt}
                      </p>
                      <span className="text-xs text-foreground-400">{related.date}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "datePublished": article.date,
            "author": {
              "@type": "Organization",
              "name": "麻将在线玩",
            },
            "publisher": {
              "@type": "Organization",
              "name": "麻将在线玩",
            },
          }),
        }}
      />
    </>
  );
}